import { formatMoney } from './buildPdfPayloadFromText';
import { normalizeCustomTables } from './pdfCustomTableUtils';

/** Tier multipliers: T1 = base, T2 = base × 1.333333, T3 = base × 2 */
export const SHIPPING_TIER_MULTIPLIERS = [1, 1.333333, 2];

export const DEFAULT_BOX_TURNAROUND = 'Printing & Shipping turnaround: 15-20 business days';

export const DEFAULT_LABEL_LEAD_TIME = 'Order lead time: 5-10 business days';

export function parseShippingCostNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/[^\d.]/g, ''));
  return Number.isNaN(n) ? null : n;
}

export function roundShippingAmount(amount) {
  return Math.round(amount * 100) / 100;
}

export function calcShippingTierCosts(basePrice, tierCount = 3) {
  const base = typeof basePrice === 'number' ? basePrice : parseShippingCostNumber(basePrice);
  if (base == null) return Array.from({ length: tierCount }, () => '');

  return Array.from({ length: tierCount }, (_, index) => {
    const multiplier = SHIPPING_TIER_MULTIPLIERS[index]
      ?? SHIPPING_TIER_MULTIPLIERS[SHIPPING_TIER_MULTIPLIERS.length - 1];
    const amount = roundShippingAmount(base * multiplier);
    return `$${formatMoney(amount)}`;
  });
}

export function buildShippingRow(method, baseCost, timeframe, tierCount = 3) {
  const base = parseShippingCostNumber(baseCost);
  return {
    method,
    baseCost: base,
    costs: base != null ? calcShippingTierCosts(base, tierCount) : Array.from({ length: tierCount }, () => ''),
    timeframe,
  };
}

export function resolveShippingRowCosts(row, tierCount) {
  const base = row.baseCost ?? parseShippingCostNumber(row.costs?.[0]);
  if (base != null) {
    return calcShippingTierCosts(base, tierCount);
  }
  return Array.from({ length: tierCount }, () => '');
}

export function recalcShippingRow(row, tierCount = 3) {
  const base = parseShippingCostNumber(row.baseCost ?? row.costs?.[0]);
  if (base == null) return row;
  return {
    ...row,
    baseCost: base,
    costs: calcShippingTierCosts(base, tierCount),
  };
}

export function recalcAllShipping(product) {
  const tierCount = product?.pdfPricing?.length
    || product?.quantitiesCount
    || 3;
  if (!product?.pdfShipping?.length) return product;
  return {
    ...product,
    pdfShipping: product.pdfShipping.map((row) => recalcShippingRow(row, tierCount)),
  };
}

export function recalcPricingRow(row) {
  const qty = Number(row.qty);
  const price = Number(row.pricePerUnit);
  if (!Number.isNaN(qty) && !Number.isNaN(price)) {
    return { ...row, orderTotal: qty * price };
  }
  return row;
}

export function recalcAllPricing(product) {
  if (!product?.pdfPricing?.length) return product;
  return {
    ...product,
    pdfPricing: product.pdfPricing.map(recalcPricingRow),
  };
}

export function formatShippingCost(value) {
  if (value == null || value === '') return '';
  const n = parseShippingCostNumber(value);
  if (n == null) return String(value);
  return `$${formatMoney(n)}`;
}

export function formatShippingCell(value) {
  if (value == null || value === '') return '';
  if (String(value).includes('$')) return String(value);
  return formatShippingCost(value);
}

export function hasShippingData(product) {
  return (product?.pdfShipping || []).some((row) => row.baseCost != null || parseShippingCostNumber(row.costs?.[0]) != null);
}

export function getTierCount(product) {
  return Math.max(1, product?.pdfPricing?.length || product?.quantitiesCount || 1);
}

export function getAutoBoxShippingCosts(product) {
  const tierCount = getTierCount(product);
  const primary = product?.pdfShipping?.[0];
  if (!primary) return Array.from({ length: tierCount }, () => '');
  return resolveShippingRowCosts(primary, tierCount);
}

export function attachBoxShippingToPricing(product) {
  const autoCosts = getAutoBoxShippingCosts(product);
  const pdfPricing = (product.pdfPricing || []).map((row, index) => {
    if (row.shippingCostManual && row.shippingCost != null && row.shippingCost !== '') {
      return row;
    }
    return { ...row, shippingCost: autoCosts[index] || row.shippingCost || '' };
  });
  return { ...product, pdfPricing };
}

export function createEmptyPricingRow(isLabel = false) {
  return {
    qty: '',
    pricePerUnit: '',
    orderTotal: null,
    shippingCost: '',
    shippingCostManual: false,
    ...(isLabel ? { specsLine: '', skuLabel: '' } : {}),
  };
}

export function syncProductFromEdits(product) {
  if (!product) return product;
  const withPricing = recalcAllPricing(product);
  const withShipping = recalcAllShipping(withPricing);
  if (withShipping.productType === 'Boxes') {
    return attachBoxShippingToPricing(withShipping);
  }
  return withShipping;
}

export function syncPayloadFromEdits(payload) {
  const products = (payload.products || []).map(syncProductFromEdits);
  return { ...payload, products, pdfCustomTables: normalizeCustomTables(payload.pdfCustomTables) };
}
